import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
    FormControlLabel, Switch, Link as MuiLink, TablePagination, CircularProgress,
} from '@mui/material';
import { WorkSiteService } from '../../services/workSite.service';
import { AuthService } from '../../services/auth.service';
import { WorkSite } from '../../interfaces/workSite.interface';
import { readPunchLocation } from '../../utils/geolocation';
import { useToast } from '../../context/ToastContext';
import WorkSiteAssignmentDialog from './WorkSiteAssignmentDialog';

const DEFAULT_RADIUS = 150;
const MIN_RADIUS = 25;
const MAX_RADIUS = 5000;
const DEFAULT_PAGE_SIZE = 25;
// Mirrors the backend's @Size bounds so the field can never round-trip to a 400.
const MAX_NAME_LENGTH = 120;
const MAX_ADDRESS_LENGTH = 255;

interface FormState {
    id?: string;
    name: string;
    address: string;
    latitude: string;
    longitude: string;
    radiusMeters: string;
    isActive: boolean;
}

const emptyForm: FormState = {
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radiusMeters: String(DEFAULT_RADIUS),
    isActive: true,
};

// Accepts the "23.7808, 90.4142" format Google Maps copies (comma, optional
// surrounding whitespace, optional leading minus on either number).
const COORDINATE_PAIR = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;

const WorkSitesPage: React.FC = () => {
    const [sites, setSites] = useState<WorkSite[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [totalElements, setTotalElements] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [pasteValue, setPasteValue] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [locationHint, setLocationHint] = useState<string | null>(null);
    // readPunchLocation can take up to 6 seconds (a 5s browser timeout plus a 1s guard).
    // Without this the button gave no feedback at all and repeated clicks queued
    // overlapping reads whose results raced each other into the form.
    const [readingLocation, setReadingLocation] = useState(false);
    const [assignmentSite, setAssignmentSite] = useState<WorkSite | null>(null);
    const toast = useToast();

    useEffect(() => {
        fetchSites(page, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const fetchSites = async (pageToLoad = page, size = rowsPerPage) => {
        try {
            setLoading(true);
            const result = await WorkSiteService.list(pageToLoad, size);
            // A refetch (typically after a delete) can land on a page that no longer has any
            // rows — e.g. deleting the last site on the last page. Rather than show the admin
            // an empty table that reads as "your data is gone", drop back to the last page that
            // still has rows and refetch that instead.
            if (result.content.length === 0 && pageToLoad > 0) {
                const lastPageWithRows = Math.max(0, Math.ceil(result.totalElements / size) - 1);
                if (lastPageWithRows !== pageToLoad) {
                    setPage(lastPageWithRows);
                    return;
                }
            }
            setSites(result.content);
            setTotalElements(result.totalElements);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch work sites');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (_: unknown, nextPage: number) => setPage(nextPage);

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(e.target.value));
        setPage(0);
    };

    const openCreateDialog = () => {
        setForm(emptyForm);
        setPasteValue('');
        setErrors({});
        setLocationHint(null);
        setReadingLocation(false);
        setDialogOpen(true);
    };

    const openEditDialog = (site: WorkSite) => {
        setForm({
            id: site.id,
            name: site.name,
            address: site.address ?? '',
            latitude: String(site.latitude),
            longitude: String(site.longitude),
            radiusMeters: String(site.radiusMeters),
            isActive: site.isActive !== false,
        });
        setPasteValue('');
        setErrors({});
        setLocationHint(null);
        setReadingLocation(false);
        setDialogOpen(true);
    };

    const closeDialog = () => setDialogOpen(false);

    const handleUseCurrentLocation = async () => {
        if (readingLocation) return;
        setLocationHint(null);
        setReadingLocation(true);
        try {
            const location = await readPunchLocation();
            if (!location) {
                setLocationHint('Could not read your location — enter the coordinates manually.');
                return;
            }
            setForm(prev => ({
                ...prev,
                latitude: String(location.latitude),
                longitude: String(location.longitude),
            }));
        } finally {
            setReadingLocation(false);
        }
    };

    const handlePasteChange = (value: string) => {
        setPasteValue(value);
        const match = COORDINATE_PAIR.exec(value);
        if (match) {
            setForm(prev => ({ ...prev, latitude: match[1], longitude: match[2] }));
        }
    };

    const validate = (): Record<string, string> => {
        const next: Record<string, string> = {};
        if (!form.name.trim()) {
            next.name = 'Site name is required';
        } else if (form.name.trim().length > MAX_NAME_LENGTH) {
            next.name = `Site name must be at most ${MAX_NAME_LENGTH} characters`;
        }
        const lat = Number(form.latitude);
        if (form.latitude.trim() === '' || Number.isNaN(lat)) {
            next.latitude = 'Latitude is required';
        } else if (lat < -90 || lat > 90) {
            next.latitude = 'Latitude must be between -90 and 90';
        }
        const lng = Number(form.longitude);
        if (form.longitude.trim() === '' || Number.isNaN(lng)) {
            next.longitude = 'Longitude is required';
        } else if (lng < -180 || lng > 180) {
            next.longitude = 'Longitude must be between -180 and 180';
        }
        const radius = Number(form.radiusMeters);
        if (form.radiusMeters.trim() === '' || Number.isNaN(radius)) {
            next.radiusMeters = 'Radius is required';
        } else if (!Number.isInteger(radius)) {
            next.radiusMeters = 'Radius must be a whole number of metres';
        } else if (radius < MIN_RADIUS) {
            next.radiusMeters = `Radius must be at least ${MIN_RADIUS} metres`;
        } else if (radius > MAX_RADIUS) {
            next.radiusMeters = `Radius must be at most ${MAX_RADIUS} metres`;
        }
        return next;
    };

    const handleSave = async () => {
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        const payload: WorkSite = {
            institutionId: AuthService.getSelectedInstitution()!!,
            name: form.name.trim(),
            address: form.address.trim() || undefined,
            latitude: Number(form.latitude),
            longitude: Number(form.longitude),
            radiusMeters: Number(form.radiusMeters),
            isActive: form.isActive,
        };

        try {
            if (form.id) {
                await WorkSiteService.update(form.id, payload);
                toast.showSuccess('Work site updated');
            } else {
                await WorkSiteService.create(payload);
                toast.showSuccess('Work site created');
            }
            setDialogOpen(false);
            await fetchSites(page, rowsPerPage);
        } catch (err) {
            toast.showApiError(err, 'Failed to save work site');
        }
    };

    const handleToggleActive = async (site: WorkSite) => {
        const nextActive = !(site.isActive !== false);
        const verb = nextActive ? 'reactivate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${verb} "${site.name}"?`)) {
            return;
        }
        try {
            await WorkSiteService.update(site.id!!, { ...site, isActive: nextActive });
            setSites(prev => prev.map(s => (s.id === site.id ? { ...s, isActive: nextActive } : s)));
            toast.showSuccess(nextActive ? 'Work site reactivated' : 'Work site deactivated');
        } catch (err) {
            toast.showApiError(err, `Failed to ${verb} work site`);
        }
    };

    const handleDelete = async (site: WorkSite) => {
        if (!window.confirm(`Permanently delete "${site.name}"? Prefer Deactivate unless you are sure.`)) {
            return;
        }
        try {
            await WorkSiteService.remove(site.id!!);
            await fetchSites(page, rowsPerPage);
            toast.showSuccess('Work site deleted');
        } catch (err) {
            toast.showApiError(err, 'Failed to delete work site');
        }
    };

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <Box data-help-page="attendance.sites.page" sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>Work Sites</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                <Button data-help="attendance.sites.add" variant="contained" onClick={openCreateDialog}>
                    Add Site
                </Button>
            </Box>

            <TableContainer data-help-section="attendance.sites.table" component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="attendance.sites.col.name">Name</TableCell>
                            <TableCell data-help="attendance.sites.col.address">Address</TableCell>
                            <TableCell data-help="attendance.sites.col.coordinates">Coordinates</TableCell>
                            <TableCell data-help="attendance.sites.col.radius">Radius</TableCell>
                            <TableCell data-help="attendance.sites.col.active">Active</TableCell>
                            <TableCell data-help="attendance.sites.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sites.map((site) => {
                            const active = site.isActive !== false;
                            return (
                                <TableRow key={site.id} sx={{ opacity: active ? 1 : 0.55 }}>
                                    <TableCell>{site.name}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{site.address || '—'}</TableCell>
                                    <TableCell>
                                        {site.latitude}, {site.longitude}
                                        {' '}
                                        <MuiLink
                                            href={`https://www.google.com/maps?q=${site.latitude},${site.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View on map
                                        </MuiLink>
                                    </TableCell>
                                    <TableCell>{site.radiusMeters} m</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={active ? 'Active' : 'Inactive'}
                                            color={active ? 'success' : 'default'}
                                            size="small"
                                            variant={active ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button data-help="attendance.sites.action.edit" size="small" onClick={() => openEditDialog(site)}>
                                            Edit
                                        </Button>
                                        <Button data-help="attendance.sites.action.assign" size="small" onClick={() => setAssignmentSite(site)}>
                                            Assign employees
                                        </Button>
                                        <Button
                                            data-help="attendance.sites.action.toggleActive"
                                            size="small"
                                            color={active ? 'warning' : 'success'}
                                            onClick={() => handleToggleActive(site)}
                                        >
                                            {active ? 'Deactivate' : 'Reactivate'}
                                        </Button>
                                        <Button data-help="attendance.sites.action.delete" size="small" color="error" onClick={() => handleDelete(site)}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {sites.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">No work sites configured</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    data-help="attendance.sites.paging"
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>

            <Dialog open={dialogOpen} onClose={closeDialog} PaperProps={{ 'data-help-page': 'attendance.siteForm.page' } as any} maxWidth="sm" fullWidth>
                <DialogTitle>{form.id ? 'Edit Work Site' : 'Add Work Site'}</DialogTitle>
                <DialogContent data-help-section="attendance.siteForm.section.details">
                    <TextField
                        autoFocus
                        data-help="attendance.sites.name"
                        label="Site name"
                        value={form.name}
                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        error={!!errors.name}
                        helperText={errors.name}
                        inputProps={{ maxLength: MAX_NAME_LENGTH }}
                        fullWidth
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        data-help="attendance.siteForm.field.address"
                        label="Address"
                        value={form.address}
                        onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                        inputProps={{ maxLength: MAX_ADDRESS_LENGTH }}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        data-help="attendance.sites.paste"
                        label="Paste coordinates"
                        placeholder="23.7808, 90.4142"
                        value={pasteValue}
                        onChange={(e) => handlePasteChange(e.target.value)}
                        fullWidth
                        sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button data-help="attendance.sites.useLocation"
                            size="small"
                            onClick={handleUseCurrentLocation}
                            disabled={readingLocation}
                            startIcon={readingLocation ? <CircularProgress size={14} /> : undefined}
                        >
                            {readingLocation ? 'Reading your location…' : 'Use my current location'}
                        </Button>
                    </Box>
                    {locationHint && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {locationHint}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            data-help="attendance.sites.latitude"
                            label="Latitude"
                            type="number"
                            value={form.latitude}
                            onChange={(e) => setForm(prev => ({ ...prev, latitude: e.target.value }))}
                            error={!!errors.latitude}
                            helperText={errors.latitude}
                            fullWidth
                        />
                        <TextField
                            data-help="attendance.sites.longitude"
                            label="Longitude"
                            type="number"
                            value={form.longitude}
                            onChange={(e) => setForm(prev => ({ ...prev, longitude: e.target.value }))}
                            error={!!errors.longitude}
                            helperText={errors.longitude}
                            fullWidth
                        />
                    </Box>
                    <TextField
                        data-help="attendance.sites.radiusField"
                        label="Radius (metres)"
                        type="number"
                        inputProps={{ step: 1 }}
                        value={form.radiusMeters}
                        onChange={(e) => setForm(prev => ({ ...prev, radiusMeters: e.target.value }))}
                        error={!!errors.radiusMeters}
                        helperText={errors.radiusMeters}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                        data-help="attendance.siteForm.field.active"
                        control={
                            <Switch
                                checked={form.isActive}
                                onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                            />
                        }
                        label="Active"
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="attendance.siteForm.cancel" onClick={closeDialog}>Cancel</Button>
                    <Button data-help="attendance.siteForm.save" variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>

            {assignmentSite && (
                <WorkSiteAssignmentDialog
                    open
                    site={assignmentSite}
                    onClose={() => setAssignmentSite(null)}
                />
            )}
        </Box>
    );
};

export default WorkSitesPage;
